import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, FileDown, Mail, Loader2, MapPin, ArrowLeft } from "lucide-react";
import Header from "@/components/quote/Header";
import { usd } from "@/lib/calc";
import { getQuote, emailQuote } from "@/lib/api";
import { generateQuotePDF } from "@/lib/pdf";

const Line = ({ label, value, strong }) => (
  <div className={`flex items-center justify-between py-2.5 ${strong ? "" : "border-b border-slate-100"}`}>
    <span className={`text-sm ${strong ? "font-semibold text-slate-900" : "text-slate-600"}`}>{label}</span>
    <span className="font-mono-plex text-sm font-semibold text-slate-900">{usd(value)}</span>
  </div>
);

export default function QuoteLookup() {
  const { reference: refParam } = useParams();
  const navigate = useNavigate();
  const [ref, setRef] = useState(refParam || "");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [error, setError] = useState("");

  const load = async (reference) => {
    if (!reference) return;
    setLoading(true);
    setError("");
    try {
      const q = await getQuote(reference.trim().toUpperCase());
      setQuote(q);
    } catch (e) {
      setQuote(null);
      setError(e?.response?.status === 404 ? "No quote found with that reference." : "Could not load quote.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "GloGrid VMI Quoter · Lookup";
    if (refParam) load(refParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refParam]);

  const onSearch = (e) => {
    e.preventDefault();
    if (ref.trim()) navigate(`/quote/${ref.trim().toUpperCase()}`);
  };

  const onEmail = async () => {
    if (!quote?.inputs?.contactEmail) {
      toast.error("This quote has no email on file.");
      return;
    }
    setEmailing(true);
    try {
      await emailQuote(quote.reference);
      toast.success("Quote emailed", { description: `Sent to ${quote.inputs.contactEmail}` });
    } catch (e) {
      toast.error("Could not email quote", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setEmailing(false);
    }
  };

  const b = quote?.breakdown;
  const inp = quote?.inputs || {};
  const expired = quote?.validUntil ? new Date(quote.validUntil) < new Date() : false;
  const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-slate-100">
      <Header />
      <main className="mx-auto max-w-3xl px-6 py-12 md:px-8">
        <button
          onClick={() => navigate("/")}
          className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
          data-testid="back-to-calc"
        >
          <ArrowLeft className="h-4 w-4" /> New quote
        </button>

        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Look up a quote</h1>
        <p className="mt-2 text-slate-500">Enter your reference number (e.g. GG-VMI-AB12CD) to reopen a saved quotation.</p>

        <form onSubmit={onSearch} className="mt-6 flex gap-3">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="GG-VMI-XXXXXX"
              className="h-12 rounded-sm border-slate-300 bg-white pl-9 font-mono-plex uppercase focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
              data-testid="lookup-input"
            />
          </div>
          <Button
            type="submit"
            className="h-12 rounded-sm bg-slate-900 px-6 font-semibold text-white transition-colors hover:bg-blue-600"
            data-testid="lookup-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find"}
          </Button>
        </form>

        {error && (
          <p className="mt-4 text-sm font-medium text-red-500" data-testid="lookup-error">
            {error}
          </p>
        )}

        {quote && (
          <div className="mt-8 overflow-hidden rounded-sm border border-slate-200 bg-white shadow-sm gg-rise" data-testid="quote-view">
            <div className="bg-slate-900 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">Reference</div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono-plex text-lg font-semibold" data-testid="quote-reference">{quote.reference}</span>
                    <span
                      className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                        expired ? "bg-red-500/20 text-red-300" : "bg-green-500/20 text-green-300"
                      }`}
                      data-testid="quote-status-badge"
                    >
                      {expired ? "Expired" : "Valid"}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center justify-end gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                    <MapPin className="h-3.5 w-3.5" /> {quote.location.label}
                  </div>
                  <div className="text-xs text-slate-400">{fmtDate(quote.createdAt)}</div>
                  {quote.validUntil && (
                    <div className={`text-xs ${expired ? "text-red-300" : "text-slate-400"}`} data-testid="quote-valid-until">
                      Valid until {fmtDate(quote.validUntil)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 py-5">
              {(inp.companyName || inp.contactName) && (
                <div className="mb-4 text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">{inp.companyName || inp.contactName}</span>
                  {inp.contactEmail && <span className="text-slate-400"> · {inp.contactEmail}</span>}
                </div>
              )}
              <Line label="Storage of goods" value={b.storage} />
              <Line label="Inbound receiving" value={b.inbound} />
              <Line label="Handling & picking" value={b.pickLabor} />
              <Line label="Repacking" value={b.repackLabor} />
              <Line label="Outbound / shipping" value={b.outbound} />
              <div className="mt-2 border-t border-slate-200 pt-2">
                <Line label="Subtotal" value={b.subtotal} />
                <Line label={`G&A / Project Mgmt (${inp.gaPercent}%)`} value={b.ga} />
              </div>
              <div className="mt-2 flex items-end justify-between border-t-2 border-slate-900 pt-4">
                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">Est. Monthly Total</span>
                <span className="font-heading text-3xl font-extrabold tracking-tight text-blue-600" data-testid="quote-total">
                  {usd(b.total)}
                </span>
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Button
                  onClick={() => generateQuotePDF(quote)}
                  className="h-11 flex-1 rounded-sm bg-slate-900 font-semibold text-white transition-colors hover:bg-blue-600"
                  data-testid="lookup-download-btn"
                >
                  <FileDown className="mr-2 h-4 w-4" /> Download PDF
                </Button>
                <Button
                  onClick={onEmail}
                  variant="outline"
                  disabled={emailing}
                  className="h-11 flex-1 rounded-sm border-slate-300 font-semibold text-slate-800 transition-colors hover:bg-slate-100"
                  data-testid="lookup-email-btn"
                >
                  {emailing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                  Email PDF
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
